import { motion } from 'framer-motion';
import { Activity, ChevronRight, FileText } from 'lucide-react';
import { useEffect, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useTheme } from '../../contexts/ThemeContext';
import { usePreview } from '../../hooks/usePreview';
import { usePesaParcours } from '../../stores/usePesaParcours';
import CapstoneWarRoom from './CapstoneWarRoom';
import QuizEngine from './QuizEngine';
import ScenarioPlayer from './ScenarioPlayer';

const LearningArea = () => {
  const {
    activeItem,
    closeActiveItem,
    validateDiagnosticQuiz,
    validateFinalQuiz,
    harvestStepData,
    toggleShowNarrator,
    resolveAsset,
    focusResource,
  } = usePesaParcours();

  // Sélecteurs atomiques pour éviter les re-renders inutiles
  const currentModule      = usePesaParcours(s => s.currentModule);
  const modules            = usePesaParcours(s => s.modules);
  const currentModuleIndex = usePesaParcours(s => s.currentModuleIndex);

  const { darkMode } = useTheme();
  const { slug } = useParams();
  const navigate = useNavigate();
  const { isPreview } = usePreview();

  // Ref pour les timers (cleanup à l'unmount)
  const pendingTimers = useRef([]);
  const safeTimeout = (fn, ms) => {
    const id = setTimeout(() => {
      pendingTimers.current = pendingTimers.current.filter(t => t !== id);
      fn();
    }, ms);
    pendingTimers.current.push(id);
    return id;
  };
  useEffect(() => () => { pendingTimers.current.forEach(clearTimeout); }, []);

  const handleScenarioComplete = () => {
    const resolvedModule =
      currentModule ||
      modules[currentModuleIndex] ||
      modules[0];

    if (!resolvedModule) {
      console.warn('[LearningArea] handleScenarioComplete : aucun module résolvable');
      closeActiveItem();
      return;
    }

    const granules = resolvedModule.granules || [];
    const currentIdx = granules.findIndex(g =>
      String(g.id)  === String(activeItem?.id) ||
      String(g._id) === String(activeItem?.id)
    );

    if (currentIdx !== -1 && currentIdx < granules.length - 1) {
      // Il y a un granule suivant dans le même module → on le focus
      const next = granules[currentIdx + 1];
      const nextId = next.id || next._id;
      console.log('[LearningArea] → Granule suivant :', nextId, next.titre || next.title);
      focusResource(nextId);
    } else {
      // Dernier granule du module → on ferme proprement
      console.log('[LearningArea] → Dernier granule du module, fermeture');
      closeActiveItem();
    }
  };

  if (!activeItem) {
    return (
      <div className="flex flex-col items-center justify-center h-full gap-4">
        <div className="w-12 h-12 border-2 border-blue-500/20 border-t-blue-500 rounded-full animate-spin" />
        <span className={`font-mono text-[10px] tracking-[0.3em] uppercase ${darkMode ? 'text-slate-400' : 'text-slate-600'}`}>
          [ ATTENTE_FLUX_STABLE ]
        </span>
      </div>
    );
  }

  const renderContent = () => {
    const type       = activeItem.type || activeItem.data?.type;
    const itemData   = activeItem.data || activeItem.content || activeItem;
    const actualSteps = itemData?.steps || activeItem.content?.steps || [];
    const isLab      = type === 'activity' || activeItem.id?.toString().startsWith('temp-act');
    const isScenario = type === 'scenario' || type === 'content-step' || activeItem.id?.toString().startsWith('temp-scenario');

    // ── LAB (activité externe) ──────────────────────────────────────────
    if (isLab) {
      return (
        <div className="h-full flex flex-col items-center justify-center px-8">
          <motion.div initial={{ opacity: 0, y: 40 }} animate={{ opacity: 1, y: 0 }} className="text-center space-y-12">
            <div className="flex items-center justify-center gap-4 opacity-60">
              <div className="w-3 h-3 bg-cyan-400 rounded-full animate-pulse" />
              <span className={`font-mono text-[10px] uppercase tracking-[0.4em] ${darkMode ? 'text-cyan-400' : 'text-blue-700'}`}>Protocole_Expérimental</span>
              <div className="w-3 h-3 bg-cyan-400 rounded-full animate-pulse delay-150" />
            </div>
            <h2 className={`text-3xl font-black uppercase tracking-tighter ${darkMode ? 'text-white opacity-80' : 'text-gray-900'}`}>
              {itemData?.titre || itemData?.title || "Expérimentation en attente"}
            </h2>
            <motion.button whileHover={{ scale: 1.04 }} whileTap={{ scale: 0.98 }}
              onClick={() => navigate(`/investigation/${slug}/lab/${activeItem.id}${isPreview ? '?preview=true' : ''}`)}
              className={`group relative px-16 py-8 ${darkMode ? 'bg-black/40' : 'bg-white/70'} backdrop-blur-xl border ${darkMode ? 'border-cyan-500/30' : 'border-blue-300/60'} rounded-3xl overflow-hidden shadow-2xl`}>
              <div className="absolute inset-0 bg-gradient-to-r from-cyan-600/20 via-blue-600/20 to-cyan-600/20 opacity-0 group-hover:opacity-100 transition-opacity duration-700" />
              <div className="relative flex items-center gap-6">
                <div className={`w-12 h-12 rounded-full ${darkMode ? 'bg-cyan-500/20 border-cyan-400/50' : 'bg-blue-100 border-blue-400/60'} flex items-center justify-center`}>
                  <Activity size={24} className={`${darkMode ? 'text-cyan-300' : 'text-blue-700'} animate-pulse`} />
                </div>
                <div className="text-left">
                  <p className={`font-mono text-[10px] uppercase tracking-[0.4em] ${darkMode ? 'text-cyan-300 opacity-80' : 'text-blue-700 opacity-90'}`}>Activation du laboratoire</p>
                  <p className={`font-black text-2xl uppercase tracking-wider ${darkMode ? 'text-white' : 'text-gray-900'}`}>Accéder au protocole</p>
                </div>
                <ChevronRight size={28} className={`${darkMode ? 'text-cyan-300' : 'text-blue-700'} group-hover:translate-x-2 transition-transform`} />
              </div>
            </motion.button>
          </motion.div>
        </div>
      );
    }

    // ── SCENARIO / CONTENT-STEP ─────────────────────────────────────────
    // FIX #1 — onComplete passe à handleScenarioComplete → focusResource(next)
    //          et non plus closeActiveItem → retour au premier granule
    if (isScenario || actualSteps.length > 0) {
      return (
        <ScenarioPlayer
          key={activeItem.id}
          data={{ ...itemData, steps: actualSteps }}
          onHarvest={(harvested) => {
            Object.entries(harvested).forEach(([stepId, value]) => {
              harvestStepData(stepId, value);
            });
          }}
          onComplete={handleScenarioComplete}
        />
      );
    }

    // ── VIDÉO ───────────────────────────────────────────────────────────
    if (type === 'video') {
      const videoUrl = typeof itemData === 'string' ? itemData : itemData?.url;
      return (
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}
          className={`aspect-video w-full ${darkMode ? 'bg-black' : 'bg-gray-200'} rounded-[3rem] overflow-hidden border ${darkMode ? 'border-white/5' : 'border-blue-200/50'} shadow-2xl relative`}>
          <iframe src={videoUrl} title="Media_Node" className="w-full h-full border-none" allowFullScreen />
        </motion.div>
      );
    }

    // ── TEXT / CONTENT-STEP ─────────────────────────────────────────────
    if (type === 'text' || type === 'content-step') {
      return (
        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="max-w-5xl mx-auto p-8">
          <div className={`${darkMode ? 'bg-white/[0.02] border-white/5' : 'bg-slate-50 border-blue-200/60'} border rounded-[3rem] p-12 shadow-2xl backdrop-blur-sm`}>
            <div className="flex items-center gap-4 mb-6">
              <div className={`p-3 rounded-2xl ${darkMode ? 'bg-blue-500/10 text-blue-400 border border-blue-500/30' : 'bg-blue-100 text-blue-700 border border-blue-400/50'}`}>
                <FileText size={24} />
              </div>
              <h1 className={`text-3xl font-black tracking-tighter italic ${darkMode ? 'text-white' : 'text-slate-900'}`}>{itemData?.titre || 'Document'}</h1>
            </div>
            {itemData?.filePath ? (
              <div className={`aspect-[3/4] w-full ${darkMode ? 'bg-[#020408]' : 'bg-gray-100'} rounded-3xl border ${darkMode ? 'border-white/5' : 'border-blue-200/50'} overflow-hidden`}>
                <iframe src={`${resolveAsset(itemData.filePath)}#toolbar=0`} className="w-full h-full border-none" />
              </div>
            ) : (
              <div className={`prose prose-lg max-w-none ${darkMode ? 'prose-invert text-slate-400' : 'text-gray-700'}`}>
                <div className="leading-relaxed font-medium text-xl italic opacity-90">{itemData?.content || itemData?.text}</div>
              </div>
            )}
          </div>
        </motion.div>
      );
    }

    // ── FINAL QUIZ ──────────────────────────────────────────────────────    
    if (type === 'final-quiz') {
      const quizData = activeItem.data;
      if (!quizData) {
        console.warn('[LearningArea] final-quiz : quizData manquant');
        return null;
      }

      return (
        <QuizEngine
          quizData={quizData}
          mode="validation"
          onClose={() => closeActiveItem()}
          onComplete={(quizResults) => {
            const moduleId = currentModule?.id || modules[currentModuleIndex]?.id;
            if (!moduleId) {
              console.warn('[LearningArea] final-quiz onComplete : moduleId introuvable');
              safeTimeout(() => closeActiveItem(), 2500);
              return;
            }

            const results = {
              score:          quizResults.score          || 0,
              passed:         quizResults.passed         || false,
              quizId:         activeItem.data._id        || `final-quiz-${moduleId}`,
              answers:        quizResults.answers        || [],
              totalQuestions: quizResults.totalQuestions || 0,
              correctAnswers: quizResults.correctAnswers || 0,
            };

            console.log("📊 Résultats Final Quiz:", results);

            // 1. Validation (sync en arrière-plan)
            validateFinalQuiz(moduleId, results);

            // 2. FIX — délai 2500ms avant fermeture pour que l'utilisateur voie le résultat
            safeTimeout(() => {
              // Tenter d'avancer au granule suivant plutôt que fermer
              const resolvedModule = currentModule || modules[currentModuleIndex] || modules[0];
              const granules = resolvedModule?.granules || [];
              const currentIdx = granules.findIndex(g =>
                String(g.id) === String(activeItem.id) || String(g._id) === String(activeItem.id)
              );

              if (currentIdx !== -1 && currentIdx < granules.length - 1) {
                focusResource(granules[currentIdx + 1].id || granules[currentIdx + 1]._id);
              } else {
                closeActiveItem();
              }
            }, 2500);
          }}
          onFail={(failResults) => {
            console.log("❌ Final Quiz échoué:", failResults);
            // Pas de closeActiveItem immédiat : laisser le QuizEngine gérer l'état échec
            // L'utilisateur peut choisir de rejouer ou fermer via onClose
          }}
        />
      );
    }

    // ── CAPSTONE ────────────────────────────────────────────────────────
    if (type === 'capstone') {
      return <CapstoneWarRoom data={itemData} />;
    }

    // ── DIAGNOSTIC QUIZ ────────────────────────────────────────────────────────
    if (type === 'diagnostic-quiz') {
      const quizData = activeItem.data;
      if (!quizData?.questions?.length) return null;
 
      const normalizedQuiz = {
        title:        quizData.title || "Diagnostic initial – Votre niveau d'entrée",
        passingScore: quizData.passingScore || 60,
        questions:    quizData.questions.map((q, idx) => ({
          ...q,
          _id: q._id || `diag-q-${idx}`,
          options: Array.isArray(q.options)
            ? q.options.map((opt, optIdx) => ({
                text:      typeof opt === 'string' ? opt : opt.text || '',
                isCorrect: opt.isCorrect || (typeof q.correctAnswer === 'number' && q.correctAnswer === optIdx),
              }))
            : [],
        })),
      };

      return (
        <div className="flex-1 overflow-y-auto p-8">
          <div className={`shrink-0 px-6 py-3 border-b ${darkMode ? 'border-emerald-500/10 bg-emerald-500/5' : 'border-emerald-200 bg-emerald-50/50'}`}>
            <p className={`text-sm font-medium text-center max-w-2xl mx-auto ${darkMode ? 'text-emerald-200/80' : 'text-emerald-800'}`}>
              <span className="font-black uppercase tracking-widest text-[10px] mr-2 opacity-60">Diagnostic adaptatif</span>
              Ce diagnostic adapte le parcours à votre niveau actuel. Il n’y a pas de « mauvaise » réponse — seulement une cartographie précise de vos forces.
            </p>
          </div>

          <QuizEngine
            quizData={normalizedQuiz}
            mode="diagnostic"
            onComplete={(quizResults) => {
                    const moduleId = currentModule?.id || modules[currentModuleIndex]?.id;
                    console.log("📊 Résultats Diagnostic Quiz:", quizResults);
                    if (moduleId) {
                        // ✅ Appel à la nouvelle action
                        validateDiagnosticQuiz(moduleId, {
                            score:   quizResults.score   || 0,
                            passed:  quizResults.passed  || false,
                            quizId:  activeItem.data._id || `diagnostic-${moduleId}`,
                            answers: quizResults.answers || [],
                        });
                    }
                    safeTimeout(() => closeActiveItem(), 2500);
            }}
            onFail={(failResults) => {
              console.log("❌ Diagnostic échoué:", failResults);
              toggleShowNarrator();
              // Ne pas fermer immédiatement : le Narrator va s'ouvrir
            }}
            onClose={closeActiveItem}
            embedded={true}
            />
          </div>
          
      );
    }

    // ── FALLBACK ────────────────────────────────────────────────────────
    console.warn('[LearningArea] TYPE_INCOMPATIBLE :', type, activeItem);
    return (
      <div className={`text-amber-500 font-mono text-center p-10 ${darkMode ? 'text-amber-400' : 'text-amber-700'}`}>
        [!] TYPE_INCOMPATIBLE – {type || 'unknown'}
      </div>
    );
  };

  return (
    <div className="h-full w-full overflow-y-auto no-scrollbar">
      <div className="p-4 md:p-10 lg:p-16 pb-24">
        {renderContent()}
      </div>
    </div>
  );
};

export default LearningArea;
